import type { BalanceType } from '@libs/data-access/queries'
import { BALANCE_TYPE } from '@libs/data-access/queries'
import { Flex, Text } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { cva } from 'class-variance-authority'
import type { FC } from 'react'

type Props = {
  membershipFirstname: string
  totalBalance: number
  restDurationFormValue: number
  balanceType: BalanceType
}

export const rcrTableCVA = {
  root: cva(['h-12 flex-1 items-center justify-center border-neutral-300'], {
    variants: {
      cell: {
        top: 'border-b-0 border-l-1 border-r-0 border-t-1 border-solid ',
        topRight: 'border-x-1 border-b-0 border-t-1 border-solid',
        bottom: 'border-y-1 border-l-1 border-r-0 border-solid font-medium',
        bottomRight: 'border-1 border-solid font-medium'
      },
      roundedTopLeft: { true: 'rounded-tl-lg' },
      roundedTopRight: { true: 'rounded-tr-lg' },
      roundedBottomRight: { true: 'rounded-br-lg' },
      roundedBottomLeft: { true: 'rounded-bl-lg' }
    }
  })
}

export const BalanceTable: FC<Props> = ({
  membershipFirstname,
  totalBalance,
  restDurationFormValue,
  balanceType
}) => {
  const { t, tDates } = usePlanningFormTranslation()

  const forecastBalance = totalBalance - restDurationFormValue

  const balanceInDays = balanceType === BALANCE_TYPE.DAYS

  return (
    <Flex className='mb-5'>
      <Flex className='mb-2 flex-1 justify-start'>
        <Text className='text-s font-medium'>
          {
            t('restForm.counterOf', {
              value: membershipFirstname
            }) as string
          }
        </Text>
      </Flex>
      <Flex row className='flex-1 items-center'>
        <Flex
          row
          className={rcrTableCVA.root({ cell: 'top', roundedTopLeft: true })}
          data-testid='rest-form-rcr-balance'
        >
          {t('restForm.balance')}
        </Flex>
        <Flex
          row
          className={rcrTableCVA.root({ cell: 'top' })}
          data-testid='rest-form-rest-duration'
        >
          {t('restForm.restDuration')}
        </Flex>
        <Flex
          row
          className={rcrTableCVA.root({
            cell: 'topRight',
            roundedTopRight: true
          })}
          data-testid='rest-forme-forecast-balance'
        >
          {t('restForm.forecastBalance')}
        </Flex>
      </Flex>
      <Flex row className='flex-1 items-center'>
        <Flex
          row
          className={rcrTableCVA.root({
            cell: 'bottom',
            roundedBottomLeft: true
          })}
          data-testid='rest-form-rcr-balance'
        >
          {balanceInDays
            ? tDates('duration.durationDay', {
                duration: totalBalance
              })
            : tDates('duration.minuteDecimalToHoursFloor', {
                duration: totalBalance
              })}
        </Flex>
        <Flex
          row
          className={rcrTableCVA.root({ cell: 'bottom' })}
          data-testid='rest-form-rest-duration'
        >
          {balanceInDays
            ? tDates('duration.durationDay', {
                duration: restDurationFormValue
              })
            : tDates('duration.minuteDecimalToHoursFloor', {
                duration: restDurationFormValue
              })}
        </Flex>
        <Flex
          row
          className={rcrTableCVA.root({
            cell: 'bottomRight',
            roundedBottomRight: true
          })}
          data-testid='rest-forme-forecast-balance'
        >
          {balanceInDays
            ? tDates('duration.durationDay', {
                duration: forecastBalance
              })
            : tDates('duration.minuteDecimalToHoursFloor', {
                duration: forecastBalance
              })}
        </Flex>
      </Flex>
    </Flex>
  )
}
